/**
 * Macro #24 — Activity & Notifications V1 API client (owner-facing).
 *
 * All events are BACKEND-AUTHORITATIVE projections of canonical transitions; the
 * frontend never fabricates or POSTs business events. Reads carry the SIWE
 * session cookie; mark-read / mark-all-read additionally carry the session-bound
 * CSRF token (shared memory-only provider) exactly like the other owner
 * mutations, refetching once on a stale-token 403.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class ActivityError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ActivityError";
    this.status = status;
    this.code = code;
  }
}

export type ActivityCategory = "REWARDS" | "LAND" | "ARTWORK" | "ACCOUNT" | "SOCIAL";
export type ActivityImportance = "NORMAL" | "HIGH";

export interface ActivityEvent {
  id: string;
  type: string;
  category: ActivityCategory;
  importance: ActivityImportance;
  title: string;
  body: string;
  deep_link: string | null;
  event_time: string | null;
  read: boolean;
}

export interface ActivityRecent {
  items: ActivityEvent[];
  unread_count: number;
}

export interface ActivityHistory {
  items: ActivityEvent[];
  next_cursor: string | null;
  unread_count: number;
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new ActivityError(resp.status, code, message);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  return parse<T>(resp);
}

async function csrfPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const payload = JSON.stringify(body);
  const doPost = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body: payload,
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = (await resp.clone().json().catch(() => null)) as ErrorEnvelope | null;
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return parse<T>(resp);
}

export const activityApi = {
  notifications: (signal?: AbortSignal) =>
    get<ActivityRecent>("/api/v1/activity/notifications", signal),
  history: (
    opts: { category?: ActivityCategory | null; cursor?: string | null; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts.category) q.set("category", opts.category);
    if (opts.cursor) q.set("cursor", opts.cursor);
    if (opts.limit) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return get<ActivityHistory>(`/api/v1/activity${qs ? `?${qs}` : ""}`, signal);
  },
  markRead: (id: string) =>
    csrfPost<{ ok: boolean; updated: number }>(
      `/api/v1/activity/${encodeURIComponent(id)}/read`,
      {},
    ),
  markAllRead: () =>
    csrfPost<{ ok: boolean; updated: number }>("/api/v1/activity/read-all", {}),
};
